import { useCallback, useEffect, useState } from 'react';
import type { Producto, UnidadInsumo } from '@tanteo/compartido';
import { listarProductos } from '../lib/api';
import { crearInsumo, guardarReceta, listarInsumos, obtenerReceta, registrarMerma, type Insumo } from '../lib/api-gastos';
import { useApp } from '../lib/contexto';
import { BarraDePagina, Boton, Campo, CampoLista, CampoNumero, CeldaPlata, Esqueleto, EstadoVacio, FilaAcciones, FilaTabla, Insignia, MensajeError, NumeroEnLinea, TablaAncha, Tarjeta, ThAcciones } from '../ui/base';
import { Modal } from '../ui/Modal';

export function Recetas() {
  const { negocio } = useApp();
  const [insumos, setInsumos] = useState<Insumo[] | null>(null);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [creandoInsumo, setCreandoInsumo] = useState(false);
  // `existenciaMinima` en `null` es «sin umbral de alerta», que no es lo mismo que cero.
  const [nuevoInsumo, setNuevoInsumo] = useState<{
    nombre: string;
    unidad: UnidadInsumo;
    existenciaActual: number;
    existenciaMinima: number | null;
  }>({ nombre: '', unidad: 'unidad', existenciaActual: 0, existenciaMinima: null });
  const [merma, setMerma] = useState<{ insumo: Insumo; cantidad: number | null } | null>(null);
  const [recetaDe, setRecetaDe] = useState<Producto | null>(null);
  const [lineas, setLineas] = useState<{ insumoId: string; cantidad: number }[]>([]);
  const [aviso, setAviso] = useState('');
  // Aparte del aviso del diálogo: este se pinta en la página, que es donde se ve cuando el diálogo
  // no llega a abrirse.
  const [avisoPagina, setAvisoPagina] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    if (!negocio) return;
    Promise.all([listarInsumos(negocio.negocioId), listarProductos(negocio.negocioId)])
      .then(([ins, prods]) => {
        setInsumos(ins);
        setProductos(prods.filter((p) => p.activo));
      })
      .catch(() => setInsumos([]));
  }, [negocio]);
  useEffect(cargar, [cargar]);

  async function abrirReceta(p: Producto) {
    if (!negocio) return;
    // Si la lectura falla, el diálogo NO se abre: abierto y vacío no se distingue de «no tiene
    // receta», y guardar desde ahí escribiría encima de la que sí existe.
    try {
      const receta = await obtenerReceta(negocio.negocioId, p.id);
      setLineas(receta.map((r) => ({ insumoId: r.insumoId, cantidad: r.cantidad })));
      setAvisoPagina('');
      setRecetaDe(p);
    } catch {
      setAvisoPagina('No pudimos leer la receta de este producto. Vuelve a intentarlo.');
    }
  }

  async function guardarLaReceta() {
    if (!negocio || !recetaDe) return;
    setOcupado(true);
    try {
      await guardarReceta(negocio.negocioId, recetaDe.id, lineas.filter((i) => i.cantidad > 0));
      setRecetaDe(null);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo guardar la receta.');
    } finally {
      setOcupado(false);
    }
  }

  if (!negocio) return null;

  return (
    <div className="space-y-4">
      <BarraDePagina titulo="Insumos y recetas">
        <Boton onClick={() => setCreandoInsumo(true)}>+ Insumo</Boton>
      </BarraDePagina>

      <MensajeError e2e="receta-no-cargo">{avisoPagina}</MensajeError>

      <div className="grid gap-4 lg:grid-cols-2">
        <Tarjeta>
          <h2 className="mb-3 font-extrabold">Insumos (existencias por movimientos)</h2>
          {insumos === null ? (
            <Esqueleto lineas={5} />
          ) : insumos.length === 0 ? (
            <EstadoVacio titulo="Sin insumos" detalle="Crea los insumos (bolas, vasos, frutas) y arma las recetas de tus productos." />
          ) : (
            <TablaAncha className="text-contenido">
              <thead>
                <tr className="text-muted border-border border-b text-etiqueta uppercase">
                  <th className="py-1.5 pr-2">Insumo</th>
                  <th className="py-1.5 pr-2 text-right">Existencias</th>
                  <th className="py-1.5 pr-2">Estado</th>
                  <ThAcciones className="w-16" />
                </tr>
              </thead>
              <tbody>
                {insumos.map((i) => (
                  <FilaTabla key={i.id}>
                    <td className="py-1.5 pr-2 font-semibold">{i.nombre}</td>
                    <CeldaPlata className="py-1.5">{i.existenciaActual} {i.unidad}</CeldaPlata>
                    <td className="py-1.5 pr-2">
                      {i.existenciaMinima !== null && i.existenciaActual <= i.existenciaMinima ? (
                        <Insignia tono="aviso">bajo mínimo</Insignia>
                      ) : (
                        <Insignia tono="exito">ok</Insignia>
                      )}
                    </td>
                    <td className="py-1.5 text-right">
                      <button className="text-destructive text-etiqueta underline" onClick={() => setMerma({ insumo: i, cantidad: null })}>
                        merma
                      </button>
                    </td>
                  </FilaTabla>
                ))}
              </tbody>
            </TablaAncha>
          )}
        </Tarjeta>

        <Tarjeta>
          <h2 className="mb-3 font-extrabold">Recetas por producto</h2>
          <p className="text-muted mb-2 text-contenido">Cada venta descuenta insumos según la receta (sin preguntarle nada al cajero).</p>
          <div className="max-h-96 space-y-1 overflow-y-auto">
            {productos.map((p) => (
              <button key={p.id} onClick={() => void abrirReceta(p)} data-e2e="producto-receta"
                className="rounded-control bg-surface hover:bg-primary/15 block w-full px-3 py-2 text-left text-contenido font-semibold">
                {p.nombre} <span className="text-muted">({p.categoria})</span>
              </button>
            ))}
          </div>
        </Tarjeta>
      </div>

      <Modal abierto={creandoInsumo} titulo="Nuevo insumo" onCerrar={() => setCreandoInsumo(false)}>
        <div className="space-y-3">
          <Campo etiqueta="Nombre" value={nuevoInsumo.nombre} onChange={(e) => setNuevoInsumo({ ...nuevoInsumo, nombre: e.target.value })} />
          <div className="grid grid-cols-3 gap-3">
            <CampoLista etiqueta="Unidad" value={nuevoInsumo.unidad} onChange={(e) => setNuevoInsumo({ ...nuevoInsumo, unidad: e.target.value as UnidadInsumo })}>
              <option value="unidad">unidad</option>
              <option value="g">gramos</option>
              <option value="ml">mililitros</option>
            </CampoLista>
            <CampoNumero etiqueta="Existencia inicial" min={0} valor={nuevoInsumo.existenciaActual}
              onValor={(n) => setNuevoInsumo({ ...nuevoInsumo, existenciaActual: n ?? 0 })} />
            <CampoNumero etiqueta="Mínimo (alerta)" min={0} valor={nuevoInsumo.existenciaMinima}
              onValor={(n) => setNuevoInsumo({ ...nuevoInsumo, existenciaMinima: n })} />
          </div>
          <FilaAcciones>
            <Boton variante="contorno" onClick={() => setCreandoInsumo(false)}>Volver</Boton>
            <Boton disabled={nuevoInsumo.nombre.trim().length < 2 || ocupado}
              onClick={() => {
                setOcupado(true);
                crearInsumo(negocio.negocioId, {
                  nombre: nuevoInsumo.nombre,
                  unidad: nuevoInsumo.unidad,
                  existenciaActual: nuevoInsumo.existenciaActual,
                  existenciaMinima: nuevoInsumo.existenciaMinima,
                })
                  .then(() => {
                    setCreandoInsumo(false);
                    setNuevoInsumo({ nombre: '', unidad: 'unidad', existenciaActual: 0, existenciaMinima: null });
                    cargar();
                  })
                  .catch((e) => setAviso(e instanceof Error ? e.message : 'Error'))
                  .finally(() => setOcupado(false));
              }}>
              Guardar
            </Boton>
          </FilaAcciones>
        </div>
      </Modal>

      <Modal abierto={merma !== null} titulo={`Registrar merma — ${merma?.insumo.nombre}`} onCerrar={() => setMerma(null)}>
        {merma && (
          <div className="space-y-3">
            <CampoNumero etiqueta={`Cantidad perdida (${merma.insumo.unidad})`} min={0} className="num"
              valor={merma.cantidad} onValor={(n) => setMerma({ ...merma, cantidad: n })} />
            <FilaAcciones>
              <Boton variante="contorno" onClick={() => setMerma(null)}>Volver</Boton>
              <Boton variante="peligro" disabled={!merma.cantidad || merma.cantidad <= 0 || ocupado}
                onClick={() => {
                  setOcupado(true);
                  registrarMerma(negocio.negocioId, merma.insumo.id, Number(merma.cantidad))
                    .then(() => {
                      setMerma(null);
                      cargar();
                    })
                    .catch((e) => setAviso(e instanceof Error ? e.message : 'Error'))
                    .finally(() => setOcupado(false));
                }}>
                Registrar merma
              </Boton>
            </FilaAcciones>
          </div>
        )}
      </Modal>

      <Modal abierto={recetaDe !== null} titulo={`Receta — ${recetaDe?.nombre}`} onCerrar={() => setRecetaDe(null)}>
        {recetaDe && (
          <div className="space-y-3">
            {(insumos ?? []).length === 0 ? (
              <p className="text-muted text-contenido">Primero crea insumos.</p>
            ) : (
              <div className="space-y-2">
                {(insumos ?? []).map((i) => {
                  const linea = lineas.find((x) => x.insumoId === i.id);
                  return (
                    <label key={i.id} className="flex items-center justify-between gap-2 text-contenido">
                      <span className="font-semibold">{i.nombre} <span className="text-muted">({i.unidad})</span></span>
                      <NumeroEnLinea ancho="medio" alineacion="centro" min={0} decimales
                        valor={linea?.cantidad ?? null}
                        onValor={(n) => {
                          const cantidad = n ?? 0;
                          setLineas((prev) => {
                            const sin = prev.filter((x) => x.insumoId !== i.id);
                            return cantidad > 0 ? [...sin, { insumoId: i.id, cantidad }] : sin;
                          });
                        }} />
                    </label>
                  );
                })}
              </div>
            )}
            <MensajeError>{aviso}</MensajeError>
            <FilaAcciones>
              <Boton variante="contorno" onClick={() => setRecetaDe(null)} disabled={ocupado}>Volver</Boton>
              <Boton onClick={() => void guardarLaReceta()} disabled={ocupado}>Guardar receta</Boton>
            </FilaAcciones>
          </div>
        )}
      </Modal>
    </div>
  );
}
